import React from "react";
import { Card } from "primereact/card";
import KpiStatCard from "../../../KpiDashboard/components/KpiStatCard";
import PieChart from "../../../components/graphs/PieChart";
import GaugeWidget from "../components/GaugeWidget";
import DistributionHistogram from "../components/DistributionHistogram";
import RankedOwnersTable from "../components/RankedOwnersTable";
import type { OverviewAnalytics } from "../../../../types/targetGroupAnalytics";
import {
  INDIGO_COLOR,
  TEAL_COLOR,
  INDIGO_LIGHT_COLOR,
  TEAL_LIGHT_COLOR,
} from "../../../../styles/theme_constants";

interface OverviewSectionProps {
  data: OverviewAnalytics;
}

/** US1 — admin global adoption & structure overview. */
const OverviewSection: React.FC<OverviewSectionProps> = ({ data }) => {
  const { adoption, usageRatios, distributions, composition, ranking } = data;
  const isEmpty = adoption.totalGroups === 0 && adoption.stationsCovered === 0;

  if (isEmpty) {
    return (
      <div
        className="flex flex-column align-items-center justify-content-center text-center"
        style={{ minHeight: "40vh" }}
      >
        <i className="pi pi-inbox text-5xl text-color-secondary mb-3" />
        <p className="text-color-secondary">No target groups in this scope yet.</p>
      </div>
    );
  }

  const stationsPerGroupDatasets = [
    {
      data: [composition.stationsPerGroup.single, composition.stationsPerGroup.multi],
      backgroundColor: [INDIGO_COLOR, TEAL_COLOR],
      hoverBackgroundColor: [INDIGO_LIGHT_COLOR, TEAL_LIGHT_COLOR],
    },
  ];

  return (
    <div className="flex flex-column gap-4">
      {/* Adoption KPIs (FR-005) */}
      <div className="grid">
        <div className="col-12 md:col-3">
          <KpiStatCard title="Owners with groups" value={adoption.ownersWithGroups} icon="pi pi-users" severity="info" />
        </div>
        <div className="col-12 md:col-3">
          <KpiStatCard title="Owners with empty groups" value={adoption.ownersWithEmptyGroups} icon="pi pi-user-minus" severity="warning" />
        </div>
        <div className="col-12 md:col-3">
          <KpiStatCard title="Total groups" value={adoption.totalGroups} icon="pi pi-sitemap" severity="info" />
        </div>
        <div className="col-12 md:col-3">
          <KpiStatCard title="Stations covered" value={adoption.stationsCovered} icon="pi pi-map-marker" severity="info" />
        </div>
      </div>

      {/* Usage gauges (FR-006 / FR-007) */}
      <div className="grid">
        {usageRatios.usersUsingTargetGroups && (
          <div className="col-12 md:col-6">
            <Card className="h-full">
              <GaugeWidget title="% of users using target groups" ratio={usageRatios.usersUsingTargetGroups} />
            </Card>
          </div>
        )}
        <div className={usageRatios.usersUsingTargetGroups ? "col-12 md:col-6" : "col-12 md:col-6"}>
          <Card className="h-full">
            <GaugeWidget title="% of stations with target groups" ratio={usageRatios.stationsWithTargetGroups} />
          </Card>
        </div>
      </div>

      {/* Distributions (FR-008) */}
      <div className="grid">
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <DistributionHistogram title="Groups per owner" buckets={distributions.groupsPerOwner} unit="Owners" xLabel="Number of groups" />
          </Card>
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <DistributionHistogram title="Group size (members per group)" buckets={distributions.membersPerGroup} unit="Groups" xLabel="Number of members" />
          </Card>
        </div>
      </div>

      {/* Composition pie (FR-009) */}
      <div className="grid">
        <PieChart title="Single vs multi-station groups" labels={["Single-station", "Multi-station"]} datasets={stationsPerGroupDatasets} />
      </div>

      {/* Top owners ranking — admin/global only (FR-016) */}
      {ranking && <RankedOwnersTable rows={ranking.topOwners} />}
    </div>
  );
};

export default OverviewSection;
